import { trackEvent } from "../../../utils/analytics";
import { getCurrentEnvironment } from "./environmentVisibility.config";

// ─── Context Helpers ─────────────────────────────────────────────────────────

export type ViewportType = "mobile" | "tablet" | "desktop";
export type ScreenSizeBucket = "small" | "medium" | "large" | "xlarge";

const getViewportType = (): ViewportType => {
  if (typeof window === "undefined") return "desktop";
  const width = window.innerWidth;
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
};

const getScreenSizeBucket = (): ScreenSizeBucket => {
  if (typeof window === "undefined") return "large";
  const width = window.innerWidth;
  if (width < 640) return "small";
  if (width < 1024) return "medium";
  if (width < 1440) return "large";
  return "xlarge";
};

const getEnvironmentContext = (): Record<string, string> => ({
  environment: getCurrentEnvironment(),
  viewport_type: getViewportType(),
  screen_size_bucket: getScreenSizeBucket(),
});

// ─── Session State (module-scoped) ───────────────────────────────────────────

let sidebarOpenTime: number | null = null;
let firstActionTracked = false;
let hasInteracted = false;
let pageSessionOpenCount = 0;
let categoryExpansionOrder: string[] = [];
let categoryExpandTimestamps: Map<string, number> = new Map();
let categoryClickedApps: Set<string> = new Set();

const resetSessionState = (): void => {
  firstActionTracked = false;
  hasInteracted = false;
  categoryExpansionOrder = [];
  categoryExpandTimestamps = new Map();
  categoryClickedApps = new Set();
};

// ─── First Action & Interaction Tracking ─────────────────────────────────────

const trackFirstActionIfNeeded = (actionType: string): void => {
  if (firstActionTracked || !sidebarOpenTime) return;
  firstActionTracked = true;
  hasInteracted = true;
  const timeToActionMs = Date.now() - sidebarOpenTime;
  trackEvent("sidebar_time_to_first_action", {
    time_to_action_ms: timeToActionMs,
    time_to_action_seconds: Math.round(timeToActionMs / 1000),
    first_action_type: actionType,
    ...getEnvironmentContext(),
  });
};

const markInteraction = (): void => {
  hasInteracted = true;
};

// ─── Sidebar Lifecycle ───────────────────────────────────────────────────────

export const trackSidebarOpen = (source: string): void => {
  pageSessionOpenCount++;
  trackEvent("sidebar_open", {
    source,
    timestamp: Date.now(),
    open_count_this_session: pageSessionOpenCount,
    ...getEnvironmentContext(),
  });
};

export const resetPageSessionOpenCount = (): void => {
  pageSessionOpenCount = 0;
};

export const trackSidebarClose = (method: "close_button" | "backdrop_click" | "escape_key"): void => {
  trackEvent("sidebar_close", {
    method,
    timestamp: Date.now(),
    ...getEnvironmentContext(),
  });
};

export const trackSidebarScroll = (scrollDepthPercent: number): void => {
  trackEvent("sidebar_scroll", {
    scroll_depth_percent: Math.round(scrollDepthPercent),
  });
};

// ─── Category Interactions ───────────────────────────────────────────────────

export const trackCategoryToggle = (
  categoryId: string,
  categoryTitle: string,
  action: "expand" | "collapse"
): void => {
  trackFirstActionIfNeeded("category_toggle");
  markInteraction();

  if (action === "expand") {
    categoryExpansionOrder.push(categoryId);
    categoryExpandTimestamps.set(categoryId, Date.now());
  }

  trackEvent("sidebar_category_toggle", {
    category_id: categoryId,
    category_title: categoryTitle,
    action,
    expansion_order: categoryExpansionOrder.indexOf(categoryId) + 1,
    total_expanded_so_far: categoryExpansionOrder.length,
  });
};

export const trackCategoryExpansionOrder = (): void => {
  if (categoryExpansionOrder.length === 0) return;
  trackEvent("sidebar_category_expansion_sequence", {
    sequence: categoryExpansionOrder.join(" -> "),
    total_categories_explored: categoryExpansionOrder.length,
    first_category: categoryExpansionOrder[0],
    last_category: categoryExpansionOrder[categoryExpansionOrder.length - 1],
  });
};

export const trackCategoryCollapseWithoutClick = (
  categoryId: string,
  categoryTitle: string
): void => {
  trackEvent("sidebar_category_collapse_no_click", {
    category_id: categoryId,
    category_title: categoryTitle,
  });
};

export const trackCategoryTimeSpent = (
  categoryId: string,
  categoryTitle: string
): void => {
  const expandTime = categoryExpandTimestamps.get(categoryId);
  if (!expandTime) return;
  const durationMs = Date.now() - expandTime;
  trackEvent("sidebar_category_time_spent", {
    category_id: categoryId,
    category_title: categoryTitle,
    duration_ms: durationMs,
    duration_seconds: Math.round(durationMs / 1000),
  });
  categoryExpandTimestamps.delete(categoryId);
};

export const markCategoryAppClicked = (categoryId: string): void => {
  categoryClickedApps.add(categoryId);
};

export const wasCategoryAppClicked = (categoryId: string): boolean => {
  return categoryClickedApps.has(categoryId);
};

// ─── App Interactions ────────────────────────────────────────────────────────

export const trackAppClick = (
  appId: string,
  appName: string,
  categoryId: string,
  categoryTitle: string,
  route: string,
  appStatus: "active" | "coming_soon" | "locked" | "beta" | "disabled"
): void => {
  trackFirstActionIfNeeded("app_click");
  markInteraction();
  markCategoryAppClicked(categoryId);
  trackEvent("sidebar_app_click", {
    app_id: appId,
    app_name: appName,
    category_id: categoryId,
    category_title: categoryTitle,
    route,
    app_status: appStatus,
    full_path: `${categoryId}/${appId}`,
    ...getEnvironmentContext(),
  });
};

export const trackAppHover = (
  appId: string,
  categoryId: string
): void => {
  trackEvent("sidebar_app_hover", {
    app_id: appId,
    category_id: categoryId,
    full_path: `${categoryId}/${appId}`,
  });
};

let appHoverStartTimes: Map<string, number> = new Map();

export const startAppHoverDwell = (appId: string, categoryId: string): void => {
  appHoverStartTimes.set(`${categoryId}/${appId}`, Date.now());
};

export const endAppHoverDwell = (appId: string, categoryId: string): void => {
  const key = `${categoryId}/${appId}`;
  const startTime = appHoverStartTimes.get(key);
  if (!startTime) return;
  const dwellMs = Date.now() - startTime;
  appHoverStartTimes.delete(key);
  if (dwellMs < 300) return;
  trackEvent("sidebar_app_tooltip_dwell", {
    app_id: appId,
    category_id: categoryId,
    full_path: key,
    dwell_ms: dwellMs,
    dwell_seconds: Math.round(dwellMs / 1000),
  });
};

export const trackRepeatAppSelection = (
  appId: string,
  appName: string,
  categoryId: string
): void => {
  trackEvent("sidebar_repeat_app_selection", {
    app_id: appId,
    app_name: appName,
    category_id: categoryId,
    full_path: `${categoryId}/${appId}`,
  });
};

export const trackAppNavigation = (
  fromAppId: string,
  toAppId: string,
  toCategoryId: string,
  toRoute: string
): void => {
  trackEvent("sidebar_app_navigation", {
    from_app_id: fromAppId,
    to_app_id: toAppId,
    to_category_id: toCategoryId,
    to_route: toRoute,
    navigation_path: `${fromAppId} -> ${toCategoryId}/${toAppId}`,
  });
};

// ─── Notifications Section ───────────────────────────────────────────────────

export const trackNotificationsToggle = (
  action: "expand" | "collapse",
  unreadCount: number
): void => {
  trackFirstActionIfNeeded("notifications_toggle");
  markInteraction();
  trackEvent("sidebar_notifications_toggle", {
    action,
    unread_count: unreadCount,
  });
};

export const trackNotificationsMarkAllRead = (count: number): void => {
  markInteraction();
  trackEvent("sidebar_notifications_mark_all_read", {
    notifications_count: count,
  });
};

export const trackNotificationsScrollLoadMore = (): void => {
  trackEvent("sidebar_notifications_load_more");
};

export const trackNotificationClick = (
  notificationId: string,
  notificationTitle: string,
  priority: string,
  isRead: boolean
): void => {
  trackFirstActionIfNeeded("notification_click");
  markInteraction();
  trackEvent("sidebar_notification_click", {
    notification_id: notificationId,
    notification_title: notificationTitle.slice(0, 100),
    priority,
    was_read: isRead,
  });
};

export const trackNotificationsEmptyState = (): void => {
  trackEvent("sidebar_notifications_empty_state");
};

// ─── Reports Section ─────────────────────────────────────────────────────────

export const trackReportsToggle = (
  action: "expand" | "collapse",
  unreadCount: number
): void => {
  trackFirstActionIfNeeded("reports_toggle");
  markInteraction();
  trackEvent("sidebar_reports_toggle", {
    action,
    unread_count: unreadCount,
  });
};

export const trackReportsMarkAllRead = (count: number): void => {
  markInteraction();
  trackEvent("sidebar_reports_mark_all_read", {
    reports_count: count,
  });
};

export const trackReportsEmptyState = (): void => {
  trackEvent("sidebar_reports_empty_state");
};

export const trackReportDownload = (
  reportId: string,
  reportType: string,
  reportStatus: string
): void => {
  trackEvent("sidebar_report_download", {
    report_id: reportId,
    report_type: reportType,
    report_status: reportStatus,
  });
};

// ─── User Profile Section ────────────────────────────────────────────────────

export const trackProfileClick = (): void => {
  trackFirstActionIfNeeded("profile_click");
  markInteraction();
  trackEvent("sidebar_profile_click");
};

export const trackLogoutClick = (): void => {
  markInteraction();
  trackEvent("sidebar_logout_click", {
    timestamp: Date.now(),
  });
};

// ─── Theme Interactions ──────────────────────────────────────────────────────

export const trackThemeSubmenuToggle = (action: "open" | "close"): void => {
  trackFirstActionIfNeeded("theme_submenu_toggle");
  markInteraction();
  trackEvent("sidebar_theme_submenu_toggle", {
    action,
  });
};

export const trackThemeChange = (
  fromTheme: string,
  toTheme: string
): void => {
  markInteraction();
  trackEvent("sidebar_theme_change", {
    from_theme: fromTheme,
    to_theme: toTheme,
  });
};

// ─── Language Interactions ───────────────────────────────────────────────────

export const trackLanguageChange = (
  fromLanguage: string,
  toLanguage: string
): void => {
  trackFirstActionIfNeeded("language_change");
  markInteraction();
  trackEvent("sidebar_language_change", {
    from_language: fromLanguage,
    to_language: toLanguage,
  });
};

// ─── Store Section ───────────────────────────────────────────────────────────

export const trackStoreSectionVisible = (): void => {
  trackEvent("sidebar_store_section_visible");
};

export const trackStoreCtaClick = (destinationUrl?: string): void => {
  markInteraction();
  trackEvent("sidebar_store_cta_click", {
    destination_url: destinationUrl ?? "https://dumaxst.com/soluciones-de-ti/#medida",
  });
};

// ─── Footer & Credits ────────────────────────────────────────────────────────

export const trackCreditsOpen = (): void => {
  markInteraction();
  trackEvent("sidebar_credits_open");
};

export const trackCreditsClose = (method: "close_button" | "backdrop_click"): void => {
  trackEvent("sidebar_credits_close", {
    method,
  });
};

export const trackCodingCloudLinkClick = (): void => {
  trackEvent("sidebar_coding_cloud_link_click", {
    destination_url: "https://codingcloud.online/",
  });
};

// ─── Session-level Sidebar Metrics ───────────────────────────────────────────

export const startSidebarSession = (): void => {
  sidebarOpenTime = Date.now();
  resetSessionState();
  appHoverStartTimes = new Map();
};

export const endSidebarSession = (
  appsViewed: number,
  categoriesExpanded: number,
  visibleCategoriesCount: number,
  userRole?: string
): void => {
  if (sidebarOpenTime) {
    const durationMs = Date.now() - sidebarOpenTime;

    trackCategoryExpansionOrder();

    trackEvent("sidebar_session_end", {
      duration_ms: durationMs,
      duration_seconds: Math.round(durationMs / 1000),
      apps_viewed: appsViewed,
      categories_expanded: categoriesExpanded,
      visible_categories_count: visibleCategoriesCount,
      categories_interaction_rate: visibleCategoriesCount > 0
        ? Math.round((categoriesExpanded / visibleCategoriesCount) * 100)
        : 0,
      had_interaction: hasInteracted,
      open_count_this_session: pageSessionOpenCount,
      ...getEnvironmentContext(),
      ...(userRole ? { user_role: userRole } : {}),
    });

    if (!hasInteracted) {
      trackEvent("sidebar_abandoned", {
        duration_ms: durationMs,
        duration_seconds: Math.round(durationMs / 1000),
        ...getEnvironmentContext(),
      });
    }

    sidebarOpenTime = null;
  }
};

export const trackSidebarOpenFrequency = (): void => {
  if (pageSessionOpenCount > 1) {
    trackEvent("sidebar_reopen_in_session", {
      open_count: pageSessionOpenCount,
      ...getEnvironmentContext(),
    });
  }
};

// ─── Visible Categories ──────────────────────────────────────────────────────

export const trackVisibleCategoriesCount = (
  visibleCount: number,
  totalCount: number
): void => {
  trackEvent("sidebar_visible_categories", {
    visible_count: visibleCount,
    total_count: totalCount,
    filtered_out_count: totalCount - visibleCount,
    ...getEnvironmentContext(),
  });
};

// ─── Locked/Coming Soon App Attempts ─────────────────────────────────────────

export const trackLockedAppAttempt = (
  appId: string,
  appName: string,
  categoryId: string,
  lockReason: "coming_soon" | "locked" | "disabled"
): void => {
  markInteraction();
  trackEvent("sidebar_locked_app_attempt", {
    app_id: appId,
    app_name: appName,
    category_id: categoryId,
    lock_reason: lockReason,
    full_path: `${categoryId}/${appId}`,
  });
};

// ─── Test Helpers ────────────────────────────────────────────────────────────

export const _testGetSessionState = (): {
  sidebarOpenTime: number | null;
  firstActionTracked: boolean;
  hasInteracted: boolean;
  pageSessionOpenCount: number;
  categoryExpansionOrder: string[];
} => ({
  sidebarOpenTime,
  firstActionTracked,
  hasInteracted,
  pageSessionOpenCount,
  categoryExpansionOrder,
});

export const _testResetAll = (): void => {
  sidebarOpenTime = null;
  firstActionTracked = false;
  hasInteracted = false;
  pageSessionOpenCount = 0;
  categoryExpansionOrder = [];
  categoryExpandTimestamps = new Map();
  categoryClickedApps = new Set();
  appHoverStartTimes = new Map();
};
